/**
 * TISA COINS - TEACHER DASHBOARD
 *
 * Main dashboard for teachers to:
 * - Review and approve pending quest submissions
 * - Review and approve coin deposit requests
 * - View latest achievements (day/week/month filters)
 * - Award manual XP and badges
 * - View class overview
 */

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import type {
  TeacherQueueItem,
  TISAUser,
  Quest,
  AchievementTimeFilter,
} from "@/types/tisa-coins";

// Components
import { PendingQueueSection } from "./components/teacher/PendingQueueSection";
import { AchievementsSection } from "./components/teacher/AchievementsSection";
import { QuickActionsSection } from "./components/teacher/QuickActionsSection";
import { ClassOverviewSection } from "./components/teacher/ClassOverviewSection";

export default function TeacherCoinsPage() {
  const { user: authUser } = useAuth();
  const [pendingQueue, setPendingQueue] = useState<TeacherQueueItem[]>([]);
  const [achievements, setAchievements] = useState<TeacherQueueItem[]>([]);
  const [students, setStudents] = useState<TISAUser[]>([]);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState<AchievementTimeFilter>("week");
  const [teacherClass, setTeacherClass] = useState<string>("");

  useEffect(() => {
    if (!authUser) {
      setLoading(false);
      return;
    }
    loadTeacherData();
  }, [authUser]);

  useEffect(() => {
    if (teacherClass) {
      loadAchievements(timeFilter);
    }
  }, [timeFilter, teacherClass]);

  const loadTeacherData = async () => {
    if (!authUser) return;

    try {
      // Get teacher's class
      const { data: teacherData, error: teacherError } = await supabase
        .from("users")
        .select("class")
        .eq("id", authUser.id)
        .single();

      if (teacherError) throw teacherError;
      const classValue = teacherData.class || "";
      setTeacherClass(classValue);

      // Load pending queue
      await loadPendingQueue(classValue);

      // Load students in class
      await loadStudents(classValue);

      // Load all quests
      await loadQuests();

      // Load achievements
      await loadAchievements(timeFilter);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to load teacher data");
      console.error("Error loading teacher data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingQueue = async (classFilter: string) => {
    try {
      const { data, error } = await supabase
        .from("teacher_queue")
        .select("*")
        .eq("user_class", classFilter)
        .is("resolved_at", null)
        .order("created_at", { ascending: true }); // FIFO - oldest first

      if (error) throw error;
      setPendingQueue(data as TeacherQueueItem[]);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to load pending queue");
      console.error("Error loading pending queue:", error);
    }
  };

  const loadAchievements = async (filter: AchievementTimeFilter) => {
    try {
      let query = supabase
        .from("teacher_queue")
        .select("*")
        .eq("user_class", teacherClass)
        .not("resolved_at", "is", null)
        .order("resolved_at", { ascending: false })
        .limit(100);

      // Apply time filter
      const now = new Date();
      let startDate: Date;

      switch (filter) {
        case "today":
          startDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
          );
          break;
        case "week":
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          break;
        case "month":
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          break;
        case "all":
        default:
          startDate = new Date(0); // Beginning of time
      }

      if (filter !== "all") {
        query = query.gte("created_at", startDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      setAchievements(data as TeacherQueueItem[]);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to load achievements");
      console.error("Error loading achievements:", error);
    }
  };

  const loadStudents = async (classFilter: string) => {
    try {
      // Students are in children table
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("class", classFilter)
        .eq("role", "student")
        .order("first_name");

      if (error) throw error;
      setStudents(data as unknown as TISAUser[]);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to load students");
      console.error("Error loading students:", error);
    }
  };

  const loadQuests = async () => {
    try {
      const { data, error } = await supabase
        .from("quests")
        .select("*")
        .eq("active", true);

      if (error) throw error;
      setQuests(data as Quest[]);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to load quests");
      console.error("Error loading quests:", error);
    }
  };

  const handleApprove = async (queueItem: TeacherQueueItem) => {
    if (!authUser) return;

    try {
      // Get student data (students are in children table)
      const { data: student, error: studentError } = await supabase
        .from("children")
        .select("*")
        .eq("id", queueItem.user_id)
        .single();

      if (studentError) throw studentError;

      if (queueItem.type === "quest") {
        // Approve quest
        const quest = quests.find((q) => q.id === queueItem.metadata.quest_id);
        if (!quest) return;

        // Add XP event
        const xpEvent = {
          amount: quest.xp_reward,
          source: "quest",
          source_id: quest.id,
          note: "Quest completed!",
          created_by: authUser.id,
          created_at: new Date().toISOString(),
        };

        // Add approval event
        const approvalEvent = {
          quest_id: quest.id,
          event_type: "approved",
          evidence: null,
          note: "Great work!",
          created_by: authUser.id,
          created_at: new Date().toISOString(),
        };

        // Update student (students are in children table)
        const currentXpEvents = Array.isArray(student.xp_events)
          ? student.xp_events
          : [];
        const currentQuestEvents = Array.isArray(student.quest_events)
          ? student.quest_events
          : [];
        await supabase
          .from("children")
          .update({
            xp_events: [...currentXpEvents, xpEvent],
            quest_events: [...currentQuestEvents, approvalEvent],
          })
          .eq("id", student.id);
      }

      // Mark queue item as resolved
      await supabase
        .from("teacher_queue")
        .update({
          action: "approved",
          resolved_at: new Date().toISOString(),
          resolved_by: authUser.id,
        })
        .eq("id", queueItem.id);

      // Refresh data
      await loadPendingQueue(teacherClass);
      await loadAchievements(timeFilter);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to approve item");
      console.error("Error approving item:", error);
      alert("Error approving: " + error.message);
    }
  };

  const handleReject = async (queueItem: TeacherQueueItem, note: string) => {
    if (!authUser) return;

    try {
      // Get student data (students are in children table)
      const { data: student } = await supabase
        .from("children")
        .select("*")
        .eq("id", queueItem.user_id)
        .single();

      if (!student) return;

      if (queueItem.type === "quest") {
        // Add rejection event
        const rejectionEvent = {
          quest_id: queueItem.metadata.quest_id,
          event_type: "rejected",
          evidence: null,
          note: note || "Needs improvement",
          created_by: authUser.id,
          created_at: new Date().toISOString(),
        };

        const currentQuestEvents = Array.isArray(student.quest_events)
          ? student.quest_events
          : [];
        await supabase
          .from("children")
          .update({
            quest_events: [...currentQuestEvents, rejectionEvent],
          })
          .eq("id", student.id);
      }

      // Mark queue item as resolved
      await supabase
        .from("teacher_queue")
        .update({
          action: "rejected",
          resolved_at: new Date().toISOString(),
          resolved_by: authUser.id,
        })
        .eq("id", queueItem.id);

      // Refresh data
      await loadPendingQueue(teacherClass);
      await loadAchievements(timeFilter);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to reject item");
      console.error("Error rejecting item:", error);
      alert("Error rejecting: " + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-white text-xl font-cinzel">
            Loading teacher dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-crimson/20 border-2 border-crimson/50 rounded-xl p-6 text-center">
            <p className="text-4xl mb-2">⏰</p>
            <p className="text-3xl font-orbitron font-black text-white">
              {pendingQueue.length}
            </p>
            <p className="text-sm text-gray-400">Pending Approvals</p>
          </div>
          <div className="bg-emerald/20 border-2 border-emerald/50 rounded-xl p-6 text-center">
            <p className="text-4xl mb-2">✅</p>
            <p className="text-3xl font-orbitron font-black text-white">
              {achievements.filter((a) => a.action === "approved").length}
            </p>
            <p className="text-sm text-gray-400">Approved ({timeFilter})</p>
          </div>
          <div className="bg-purple/20 border-2 border-purple/50 rounded-xl p-6 text-center">
            <p className="text-4xl mb-2">👥</p>
            <p className="text-3xl font-orbitron font-black text-white">
              {students.length}
            </p>
            <p className="text-sm text-gray-400">Students</p>
          </div>
          <div className="bg-blue-500/20 border-2 border-blue-500/50 rounded-xl p-6 text-center">
            <p className="text-4xl mb-2">📋</p>
            <p className="text-3xl font-orbitron font-black text-white">
              {quests.length}
            </p>
            <p className="text-sm text-gray-400">Active Quests</p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Pending Queue */}
          <div className="lg:col-span-2 space-y-8">
            <PendingQueueSection
              queue={pendingQueue}
              quests={quests}
              onApprove={handleApprove}
              onReject={handleReject}
            />

            <AchievementsSection
              achievements={achievements}
              timeFilter={timeFilter}
              onFilterChange={setTimeFilter}
            />
          </div>

          {/* Right Column - Quick Actions & Class Overview */}
          <div className="space-y-8">
            <QuickActionsSection students={students} />
            <ClassOverviewSection students={students} />
          </div>
        </div>
      </main>
    </div>
  );
}
